import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle
} from 'react';
import api from '../api/api';
import { Form, Button, Row, Col, Toast, ToastContainer, Card, Spinner, Alert } from 'react-bootstrap';
import { useMemo } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { useVideos } from '../hooks/useVideos';
import VideoSelect from './VideoSelect';

import { useGames } from '../hooks/useGames';
import GameSelect from './GameSelect';

const VideoList = forwardRef(({ darkMode, cardBgClass }, ref) => {
  const [selectedVideo, setSelectedVideo] = useState('');
  const [title, setTitle] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [duration, setDuration] = useState('');
  const [game, setGame] = useState('');
  const { games, refresh: refreshGames, addGame } = useGames();
  const [gameInput, setGameInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [streamTitlePreview, setStreamTitelPreview] = useState('');
  const [selectedGame, setSelectedGame] = useState('');

  const { videos, refresh, loading: videosLoading, error: videosError } = useVideos();
  const fetchVideos = refresh;
  useImperativeHandle(ref, () => ({ fetchVideos: refresh }));

  const MAX_STREAM_TITLE_LENGTH = 140;

  const getStreamTitleLength = (title, releaseDate) => {
    const dateObj = releaseDate ? new Date(releaseDate) : new Date();
    const formattedDate = `${String(dateObj.getDate()).padStart(2, '0')}.${String(dateObj.getMonth() + 1).padStart(2, '0')}.${dateObj.getFullYear()}`;
    const preview = `📌 ${formattedDate} - ${title} | 24/7 mRoonix Content`;
    return preview.length;
  };

  const { currentTitleLength, remainingCharacters } = useMemo(() => {
    const length = getStreamTitleLength(title, releaseDate);
    return {
      currentTitleLength: length,
      remainingCharacters: MAX_STREAM_TITLE_LENGTH - length
    };
  }, [title, releaseDate]);


  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!releaseDate || !title) {
      setStreamTitelPreview('');
      return;
    }

    // Datum formatieren
    const dateObj = new Date(releaseDate);
    const formattedDate = `${String(dateObj.getDate()).padStart(2, '0')}.${String(dateObj.getMonth() + 1).padStart(2, '0')}.${dateObj.getFullYear()}`;

    // Kanalnamen dynamisch eintragen (derzeit manuell)
    const channelName = 'mRoonix';
    const preview = `📌 ${formattedDate} - ${title} | 24/7 ${channelName} Content`;
    setStreamTitelPreview(preview);
  }, [title, releaseDate]);

  const fetchVideoMetadata = async (filename) => {
    try {
      await refreshGames(); // sicherstellen, dass Games bei Auswahl geladen werden
      const response = await api.get(`/video-metadata/${filename}`);
      if (response.data) {
        const [d, m, y] = response.data.release_date.split('.');
        setReleaseDate(`${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`);
        setTitle(response.data.title);
        setDuration(response.data.duration);
        setSelectedGame(response.data.game);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Videometadaten:', err);
    }
  };

  const showToastMessage = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setSelectedVideo('');
    setTitle('');
    setReleaseDate('');
    setDuration('');
    setGame('');
    setGameInput('');
  };

  const handleSaveChanges = async () => {
    if (!selectedVideo) return;

    if (currentTitleLength > MAX_STREAM_TITLE_LENGTH) {
      showToastMessage(`❌ Der Streamtitel ist mit ${currentTitleLength} Zeichen zu lang (max. ${MAX_STREAM_TITLE_LENGTH}). Bitte kürzen!`, 'danger');
      return;
    }

    setSaving(true);
    try {
      const releaseDateGerman = new Date(releaseDate).toLocaleDateString('de-DE');
      await api.post('/update-metadata', {
        original_filename: selectedVideo,
        title,
        release_date: releaseDateGerman,
        duration,
        game: selectedGame // 👈 gespeichert wird das Eingegebene
      });

      showToastMessage('✅ Änderungen gespeichert!');
      refresh();
      scrollToTop();
    } catch (err) {
      console.error('Fehler beim Speichern der Änderungen:', err);
      showToastMessage('❌ Änderungen konnten nicht gespeichert werden.', 'danger');
    }
    setSaving(false);
  };

  const handleDeleteVideo = async () => {
    if (!selectedVideo) return;
    if (!window.confirm(`❗ Willst du das Video "${selectedVideo}" wirklich löschen?`)) return;

    setDeleting(true);
    try {
      await api.delete(`/video/${selectedVideo}`);
      showToastMessage('✅ Video gelöscht!');
      resetForm();
      refresh();
      scrollToTop();
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
      showToastMessage('❌ Fehler beim Löschen des Videos.', 'danger');
    }
    setDeleting(false);
  };

  const handleSelectChange = (e) => {
    const filename = e.target.value;
    setSelectedVideo(filename);
    if (filename) fetchVideoMetadata(filename);
    else resetForm();
  };

  return (
    <>
      <ToastContainer className="position-fixed top-0 start-50 translate-middle-x p-3" style={{ zIndex: 9999 }}>
        <Toast onClose={() => setShowToast(false)} show={showToast} bg={toastVariant} delay={3000} autohide>
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      <Card className={`p-4 shadow-sm mb-5 ${cardBgClass}`}>
        <h4 className="text-center mb-4">🎞️ Bestehende Videos verwalten</h4>

        {videosError && (
          <Alert variant="danger" className="d-flex justify-content-between align-items-center">
            <span>Videos konnten nicht geladen werden. Prüfe die Verbindung und versuche es erneut.</span>
            <Button size="sm" variant="outline-danger" onClick={refresh}>Erneut laden</Button>
          </Alert>
        )}

        {!videosError && !videosLoading && videos.length === 0 && (
          <Alert variant="info">
            Noch keine Videos vorhanden. Lade unter „Upload“ dein erstes Video hoch.
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label>
            Wähle ein Video: ({videos.length})
            {videosLoading && <Spinner animation="border" size="sm" className="ms-2" role="status" aria-label="Lade Videos" />}
          </Form.Label>
          <div>
            <VideoSelect
              videos={videos}
              value={selectedVideo}
              onChange={(val) => {
                setSelectedVideo(val);
                if (val) fetchVideoMetadata(val); else resetForm();
              }}
              className={darkMode ? cardBgClass : ''}
              darkMode={darkMode}
              placeholder="-- Bitte Video auswählen --"
            />
          </div>
        </Form.Group>

        {selectedVideo && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Titel:</Form.Label>
              <Form.Control type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
            </Form.Group>

            {streamTitlePreview && (
              <div className="mb-3">
                <Form.Text muted>
                  <strong>Streamtitel:</strong> {streamTitlePreview}
                  <br />
                  <span className={currentTitleLength > MAX_STREAM_TITLE_LENGTH ? 'text-danger' : 'text-muted'}>
                    {currentTitleLength} / {MAX_STREAM_TITLE_LENGTH} Zeichen verwendet (
                    {remainingCharacters < 0 ? `${-remainingCharacters} zu viel` : `${remainingCharacters} übrig`})
                  </span>

                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="tooltip-streamtitle-length">
                        Der vollständige Titel (inkl. Datum und Kanal) darf max. {MAX_STREAM_TITLE_LENGTH} Zeichen lang sein.
                      </Tooltip>
                    }
                  >
                    <span
                      style={{
                        cursor: 'pointer',
                        marginLeft: '8px',
                        color: '#0d6efd',
                        fontWeight: 'bold'
                      }}
                    >
                      ℹ️
                    </span>
                  </OverlayTrigger>
                </Form.Text>
              </div>
            )}


            <Form.Group className="mb-3">
              <Form.Label>Veröffentlichungsdatum:</Form.Label>
              <Form.Control type="date" value={releaseDate} onChange={(e) => setReleaseDate(e.target.value)} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Videolänge:</Form.Label>
              <Form.Control type="text" value={duration} disabled />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Spiel:</Form.Label>
              <GameSelect games={games} value={selectedGame} onChange={setSelectedGame} placeholder="Spiel auswählen oder eintippen..."
                className={darkMode ? cardBgClass : ''} darkMode={darkMode} cardBgClass={cardBgClass} allowCreate onCreate={addGame} />
            </Form.Group>


            <Row className="mt-4">
              <Col>
                <Button variant="success" onClick={handleSaveChanges} disabled={saving}>
                  {saving ? <><Spinner animation="border" size="sm" /> Speichern...</> : '💾 Änderungen speichern'}
                </Button>
              </Col>
              <Col className="text-end">
                <Button variant="danger" onClick={handleDeleteVideo} disabled={deleting}>
                  {deleting ? <><Spinner animation="border" size="sm" /> Löschen...</> : '🗑️ Video löschen'}
                </Button>
              </Col>
            </Row>
          </>
        )}
      </Card>
    </>
  );
});

export default VideoList;